require('./scheduledTransfersSummary.scss');

// @ vendor
const React = require('react');
const { connect } = require('react-redux');
const PureRenderMixin = require('react-addons-pure-render-mixin');
const PropTypes = require('prop-types');
const classNames = require('classnames');

// @ helpers
const { formatText, FormattedText, getLanguage } = require('core/i18n').i18n;
const { contactCenterClassName } = require('utilities/conditionals/conditionalClassname');

// @ commons
const { OKButton } = require('components/commons/OKButton/OKButton');
const { OKTableRow } = require('commonsComponents/OKTableRow');
const { OKTableCell } = require('commonsComponents/OKTableCell');
const { LoadingSection } = require('commonsComponents/loadingSection');
const { EmptyState } = require('commonsComponents/emptyState');
const { OKLink } = require('commonsComponents/OKLink');
const { OKBoxTitle } = require('commonsComponents/OKBoxTitle');
const { LastMovementsTable } = require('commonsComponents/lastMovementsTable');

// @ components
const TransfersSummaryTableRow = require('components/transfers/scheduled/scheduledTransfers/scheduledTransfersContent/scheduledTransfersContentTableRow/scheduledTransfersContentTableRow');
const DropdownAccounts = require('components/dropdownAccounts/dropdownAccounts');
const { ScheduledTransfersContentTableInfoRow } = require('components/transfers/scheduled/scheduledTransfers/scheduledTransfersContent/scheduledTransfersContentTableInfoRow/scheduledTransfersContentTableInfoRow.js');
const OKTooltip = require('components/commons/OKTooltip/OKTooltip');

// @ actions
const intervenersActions = require('actions/getAccountInterveners');

// @ constants
const { TRANSFERS_LANDING_LIMIT } = require('constants/index');
const { LAST_MOVEMENTS_TABLE_CURRENT_MONTH_KEY } = require('constants/commonsComponents');
const language = getLanguage();


class ScheduledTransfersSummary extends React.Component {
    constructor(props) {
        super(props);

        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
        this.handleAccountChange = this.handleAccountChange.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
        const { immAccounts } = props;

        this.state = {
            accountId: immAccounts.getIn(['byId', immAccounts.getIn(['byOrder', 0]), 'accountId']),
            expandedRowId: ''
        }
    }

    componentDidMount() {
        const {
            requestInterveners,
            requestScheduledTransfers
        } = this.props;
        const { accountId } = this.state;
        requestScheduledTransfers(accountId);
        requestInterveners(accountId);
    }

    componentWillUnmount() {
        const { resetInterveners } = this.props;
        const { accountId } = this.state;
        resetInterveners(accountId);
    }

    handleAccountChange(accountId) {
        const {
            requestInterveners,
            requestScheduledTransfers,
            resetInterveners
        } = this.props;
        requestInterveners(accountId);
        resetInterveners(this.state.accountId);
        requestScheduledTransfers(accountId);
        this.setState({
            accountId
        });
    }

    handleRowClick(rowId) {
        this.setState({
            expandedRowId: rowId === this.state.expandedRowId ? '' : rowId
        });
    }

    render() {
        const {
            immAccounts,
            immScheduledTransfers,
            immScreen,
            isInactiveAccountsEnabled,
        } = this.props;

        const { accountId } = this.state;
        const immAccountScheduledTransfers = immScheduledTransfers.get(accountId);
        const accountsIndexes = immAccounts.get('byOrder').toJS();
        const scheduledTransfersLink = `/myprofile/transfers/scheduled/${accountId}`;
        const headerTitle = formatText('transfersLanding-scheduledTranfers');
        const contactCenterRelativePositionForCC = contactCenterClassName('relative-position-cc');
        let table;
        let nextPageButton;

        if (immAccountScheduledTransfers) {
            const isEmpty = immAccountScheduledTransfers.get('byOrder').size === 0;
            if (immAccountScheduledTransfers.get('loading')) {
                table = <LoadingSection />;
            } else if (isEmpty) {
                const RETURN_TO_SCHEDULE_TRANSFERS_LINK = '/myprofile/transfers/transfer';
                const SCHEDULE_TRANSFER_BUTTON = (
                    <OKButton
                        modifier="secondary"
                        to={RETURN_TO_SCHEDULE_TRANSFERS_LINK}
                    >
                        <FormattedText
                            format="uppercase"
                            value="transfersOptions-scheduleTransfer"
                        />
                    </OKButton>
                );
                table = (
                    <EmptyState
                        primaryText={formatText('transfersLanding-noScheduledTransfers')}
                        button={SCHEDULE_TRANSFER_BUTTON}
                        iconType="transfer-banking" />
                );
            } else {
                const immAccountScheduledTransfersByOrder = immAccountScheduledTransfers.get('byOrder');
                const immAccountScheduledTransfersById = immAccountScheduledTransfers.get('byId');
                const size = immAccountScheduledTransfersByOrder.size;
                const transactionsCount = Math.min(size, TRANSFERS_LANDING_LIMIT);
                const { expandedRowId } = this.state;

                let tableHead;

                if (transactionsCount < size) {
                    nextPageButton = (
                        <footer className="text-right scheduled-transfers-summary__more-button-container">
                            <OKLink modifier="complementary" to={scheduledTransfersLink}>
                                <FormattedText
                                    value="transfersLanding-seeMore"
                                />
                            </OKLink>
                        </footer>
                    );
                }

                if (immScreen.get('isMobile')) {
                    tableHead = (
                        <thead>
                            <OKTableRow asHeader={true} className="scheduled-transfers-summary__table-header-row-line">
                                <OKTableCell
                                    asHeader={true}
                                    className="scheduled-transfers-summary__table-header-cell-line"
                                    colSpan={2}
                                />
                            </OKTableRow>
                        </thead>
                    );
                } else {
                    const dateClass = classNames('scheduled-transfers-summary__table-header-cell', { 'scheduled-transfers-summary__table-header-cell--date': __CONTACT_CENTER__ });
                    const periodicityClass = classNames('scheduled-transfers-summary__table-header-cell', { 'scheduled-transfers-summary__table-header-cell--periodicity': __CONTACT_CENTER__ });

                    tableHead = (
                        <thead>
                            <OKTableRow asHeader={true}>
                                <OKTableCell asHeader={true} className="scheduled-transfers__table-header-cell" />
                                <OKTableCell asHeader={true} className={dateClass}>
                                    <FormattedText className="scheduled-transfers-summary__header" value="transfersScheduled-startDate" format="uppercase" />
                                </OKTableCell>
                                <OKTableCell asHeader={true} className={dateClass}>
                                    <FormattedText className="scheduled-transfers-summary__header" value="transfersScheduled-endDate" format="uppercase" />
                                </OKTableCell>
                                <OKTableCell asHeader={true} className={periodicityClass}>
                                    <FormattedText className="scheduled-transfers-summary__header" value="transfersScheduled-periodicDelayed" format="uppercase" />
                                </OKTableCell>
                                <OKTableCell asHeader={true} className="scheduled-transfers-summary__table-header-cell">
                                    <FormattedText className="scheduled-transfers-summary__header" value="transfersScheduled-concept" format="uppercase" />
                                </OKTableCell>
                                <OKTableCell asHeader={true} className="scheduled-transfers-summary__table-header-cell-amount">
                                    <FormattedText value="transfersLanding-amount" format="uppercase" />
                                </OKTableCell>
                                <OKTableCell asHeader={true} className="scheduled-transfers__table-header-cell" />
                            </OKTableRow>
                        </thead>
                    );
                }

                table = (
                    <LastMovementsTable
                        groupByDate={false}
                        rowComponent={TransfersSummaryTableRow}
                        immTransactionsByOrder={immAccountScheduledTransfersByOrder.take(transactionsCount)}
                        immTransactionsById={immAccountScheduledTransfersById}
                        columnsCount={7}
                        expandedRowId={expandedRowId}
                        expandedRowComponent={ScheduledTransfersContentTableInfoRow}
                        onRowClick={this.handleRowClick}
                        id="scheduled-transfers-summary-table"
                        currentMonthKey={LAST_MOVEMENTS_TABLE_CURRENT_MONTH_KEY}
                        language={language}
                        className={contactCenterRelativePositionForCC}
                    >
                        {tableHead}
                    </LastMovementsTable>
                );
            }
        }

        const tooltipElement = isInactiveAccountsEnabled ? (
            <div className="scheduled-transfers-summary__tooltip">
                <OKTooltip
                    content="inactiveAccounts-inactiveAccounts_consultiveDropdownCommonToolTip"
                    offset={[0, -15]}
                />
            </div>
        ) : '';

        return (
            <article className="scheduled-transfers-summary">
                <header>
                    <div className="scheduled-transfers-summary__title">
                        <OKBoxTitle value={headerTitle} to={scheduledTransfersLink} />
                    </div>
                    <div className="row scheduled-transfers-summary__main-account">
                        <div className="col-xs-7-cc-11">
                            <DropdownAccounts
                                immAccounts={immAccounts.get('byId')}
                                accountsIndexes={accountsIndexes}
                                currentAccountId={accountId}
                                onAccountChange={this.handleAccountChange}
                                showAvailableBalance={true}
                            />
                        </div>
                        {tooltipElement}
                    </div>
                </header>
                <section>
                    {table}
                </section>
                {nextPageButton}
            </article>
        );
    }
}

ScheduledTransfersSummary.propTypes = {
    immAccounts: PropTypes.object.isRequired,
    immScheduledTransfers: PropTypes.object.isRequired,
    immScreen: PropTypes.object.isRequired,
    isInactiveAccountsEnabled: PropTypes.bool.isRequired,
    requestInterveners: PropTypes.func.isRequired,
    requestScheduledTransfers: PropTypes.func.isRequired,
    resetInterveners: PropTypes.func.isRequired
};

module.exports.constructor = ScheduledTransfersSummary;
module.exports.ScheduledTransfersSummary = connect(null, {
    requestInterveners: intervenersActions.requestAccountInterveners,
    resetInterveners: intervenersActions.resetAccountInterveners
})(ScheduledTransfersSummary);
